/**
 * Spine-check handler — MCP `quality` compound tool, action=spine-check
 * (A2, WP-581/FEAT-1402).
 *
 * Thin client: SCHEDULES the check (review fix, PR #530 P1 — the MCP gateway
 * enforces a 10s request budget; a spine check runs one sequential Roger
 * Martin LLM probe per strategy, each up to 25s, plus an optional one-story
 * pass on top — synchronously awaiting that inside an MCP request guarantees
 * a timeout). `quality.scheduleSpineCheck` returns immediately once the run
 * is queued; this handler then reads back whatever the LATEST persisted row
 * is (store-then-read-back — never the write call's own return value).
 *
 * NOUN-VERB CONTRACT (PR #530 round 2, P1): a poll-style read must never
 * itself re-trigger work — calling `scheduleSpineCheck` unconditionally on
 * every invocation re-scheduled a fresh run every time this tool was called,
 * even to just read the last result (the same "read that writes" violation
 * already fixed for direction.list/refresh). `scheduleSpineCheck` now decides
 * for itself whether to schedule (no run in flight AND the stored verdict is
 * stale/absent/missing the requested story pass), and this handler renders
 * strictly from that decision (`scheduled`/`reason`) — never infers "still
 * running" from a timestamp comparison the way round 1 did. Pass
 * `refresh:true` to force a new run even when a fresh verdict already exists.
 *
 * The CLI (`pb quality spine-check`, no MCP timeout) keeps calling
 * `quality.spineCheck` (runSpineCheck) directly and synchronously — it never
 * goes through this schedule/status split at all.
 */

import type { GatewayRouteReturnByName } from "@productbrain/kernel-client";
import { kernelQuery, kernelMutation } from "../client.js";
import { success } from "../envelope.js";

export async function handleSpineCheck(story: boolean, refresh = false) {
  const scheduleResult = await kernelMutation<GatewayRouteReturnByName<"quality.scheduleSpineCheck">>(
    "quality.scheduleSpineCheck",
    { includeStory: story, refresh },
  );
  const row = await kernelQuery<GatewayRouteReturnByName<"quality.getLatestSpineVerdict">>("quality.getLatestSpineVerdict", {});

  // "Still running" is ONLY true when THIS call actually scheduled a new run —
  // never inferred from a timestamp (round 1's stillRunning := row.createdAt <
  // triggeredAt raced the very fix this round makes: most calls now schedule
  // nothing at all, so that comparison would be meaningless).
  const stillRunning = scheduleResult.scheduled;

  if (!row) {
    const text = stillRunning
      ? "Spine check started — no prior result exists yet. It runs asynchronously (LLM calls can take 25s+ each); re-run this in a few seconds to read the result."
      : "No spine check has been run yet, and none is currently running. Pass refresh:true to start one.";
    return {
      content: [{ type: "text" as const, text }],
      structuredContent: success(text, { hasVerdict: false, checkStarted: stillRunning }),
    };
  }

  const { verdict } = row;
  if (verdict.noSpineEntries) {
    return {
      content: [{ type: "text" as const, text: "No spine entries yet — nothing to check." }],
      structuredContent: success("No spine entries yet.", { hasVerdict: true, noSpineEntries: true }),
    };
  }

  const lines: string[] = ["## Spine Check"];
  if (stillRunning) {
    lines.push(`> A new check was just triggered and is still running asynchronously — the result below is from the PREVIOUS run (${new Date(row.createdAt).toISOString()}). Re-run in a few seconds for the fresh result.`, "");
  } else if (scheduleResult.reason === "already-in-flight") {
    lines.push(`> A check is already running (started earlier, not by this call) — showing the best available result (${new Date(row.createdAt).toISOString()}). Re-run in a few seconds.`, "");
  } else if (row.stale) {
    lines.push(`> STALE — ${row.staleMemberIds.length} member entry(ies) changed since this was computed. Pass refresh:true to recompute.`, "");
  }

  const s = verdict.structural;
  lines.push(`Structural completeness: ${s.passed ? "PASS" : "FAIL"}`);
  if (s.missing.length > 0) lines.push(`Missing: ${s.missing.join(", ")}`);
  if (s.duplicateSingletons.purpose) lines.push(`Duplicate purpose singletons (which is canonical?): ${s.duplicateSingletons.purpose.join(", ")}`);
  if (s.duplicateSingletons.vision) lines.push(`Duplicate vision singletons (which is canonical?): ${s.duplicateSingletons.vision.join(", ")}`);
  if (s.unconnectedStrategyIds.length > 0) lines.push(`Unconnected strategies (no informs edge to any vision): ${s.unconnectedStrategyIds.join(", ")}`);
  // `verdict` is stored as v.any() and never migrated — a row persisted before this
  // PR's fields existed reads back with them undefined. Default rather than crash
  // (live-proof finding, PR #530 round-1 walk): a legacy row that can't disclose
  // truncation is still rendered, just without a disclosure it never recorded.
  if ((s.strategiesWithUnknownConnectivity ?? []).length > 0) {
    lines.push(`Connectivity unknown (relation set truncated — not confirmed disconnected): ${s.strategiesWithUnknownConnectivity.join(", ")}`);
  }
  if (s.visionConnectedToPurpose === false) lines.push("No vision informs any purpose.");
  if (s.visionPurposeConnectivityTruncated) lines.push("Vision→purpose connectivity unknown (relation set truncated).");
  if (verdict.strategiesTruncated) lines.push("Strategy set truncated — not every strategy was graded this pass.");
  if (verdict.perStrategyEvaluationTruncated) lines.push("Per-strategy evaluation stopped on the wall-clock budget — not every fetched strategy was graded this pass.");

  if (verdict.perStrategy.length > 0) {
    lines.push("", `Per-strategy refusal heuristic (${verdict.perStrategy.length}):`);
    for (const p of verdict.perStrategy) {
      const refusal = p.refusal.hasRefusalSignal ? "names an explicit alternative it declines" : "no explicit refusal language detected";
      // `runSpineCheck` ALWAYS attempts a Roger Martin probe per strategy — a null
      // result here is never "not requested", it's timeout/budget-reject/malformed-
      // response. Rendering it as an omission (round-1 gap) reads a failure as a
      // clean pass-through; say so explicitly (PR #530 round 2).
      const rm = p.rogerMartin
        ? `; Roger Martin test: ${p.rogerMartin.isStrategicChoice ? "genuine strategic choice" : "NOT a genuine strategic choice"}`
        : "; Roger Martin test: FAILED TO EVALUATE (timeout, budget limit, or malformed response) — not scored";
      lines.push(`- ${p.entryId}: ${refusal}${rm}`);
    }
  }

  if (verdict.story) {
    lines.push("", `One-story judgment [unvalidated, on-demand]: ${verdict.story.passed ? "PASS" : "FAIL"}`);
    if (!verdict.story.passed && verdict.story.failedCriterion) lines.push(`Failed criterion: ${verdict.story.failedCriterion}`);
  } else if (row.includesStoryPass) {
    // The row that ran the story pass exists, but its story came back null —
    // that means the LLM pass FAILED (timeout/parse error), not "never requested".
    // Conflating the two (thread #4) reads a failure as a clean skip.
    //
    // Review fix (D1 residue — "require refresh when retrying a failed story pass"): a
    // plain `story:true` re-run does NOT actually retry — scheduleSpineCheck's
    // `satisfiesStory` check treats `includesStoryPass:true` (the pass was ATTEMPTED) as
    // already satisfying the request, regardless of whether it SUCCEEDED, so a bare re-run
    // is a silent no-op here. The message now says explicitly what to pass.
    lines.push("", "One-story judgment: requested, but the LLM pass did not return a usable result (timeout or parse failure). Pass refresh:true to retry — a plain re-run will not reschedule it.");
  } else {
    lines.push("", "One-story judgment: not run this pass — pass story=true to run it.");
  }

  return {
    content: [{ type: "text" as const, text: lines.join("\n") }],
    structuredContent: success(
      `Spine check: structural ${s.passed ? "PASS" : "FAIL"}${verdict.story ? `, story ${verdict.story.passed ? "PASS" : "FAIL"} [unvalidated]` : ""}${stillRunning ? " (new run still in progress — showing previous result)" : ""}.`,
      {
        hasVerdict: true,
        noSpineEntries: false,
        structuralPassed: s.passed,
        storyPassed: verdict.story?.passed ?? null,
        stale: row.stale,
        stillRunning,
        scheduled: scheduleResult.scheduled,
        scheduleReason: scheduleResult.reason,
      },
    ),
  };
}
